import React from 'react';
import { useIntl } from 'react-intl';

import {
  getSales,
  OfflineTimeInsight,
  PreparationInsight,
  RejectionInsight,
  TotalRejectedOrdersInsight,
  RefundsOverviewInsight,
} from 'apis/merchant-gateway/insights';

import SummaryTable from './SummaryTable';
import { useInsightSubRoutes } from '../../hooks/useInsightsSubRoutes';
import { InsightResponseWithPreviousInterval, RowType } from '../../types';
import {
  calculateGrowthRate,
  formatGrowthRate,
  formatMinutes,
  formatSeconds,
  getGrowthRateForFields,
  getGrowthTrend,
} from '../../utils';

type OperationsProps = {
  preparationInsight: InsightResponseWithPreviousInterval<PreparationInsight>;
  rejectedOrdersInsight: InsightResponseWithPreviousInterval<TotalRejectedOrdersInsight>;
  rejectionsInsight: InsightResponseWithPreviousInterval<RejectionInsight>;
  offlineTimeInsight: InsightResponseWithPreviousInterval<OfflineTimeInsight>;
  refundsOverviewInsight: InsightResponseWithPreviousInterval<RefundsOverviewInsight>;
  isNewAlivenessEnabled: boolean;
  isRefundOverviewEnabled: boolean;
  isVATEnabled: boolean;
  hidePrepTime?: boolean;
  showHybridDeliveryDisclaimer?: boolean;
};

const Operations = ({
  preparationInsight,
  rejectedOrdersInsight,
  rejectionsInsight,
  offlineTimeInsight,
  refundsOverviewInsight,
  isNewAlivenessEnabled,
  isRefundOverviewEnabled,
  isVATEnabled,
  hidePrepTime,
  showHybridDeliveryDisclaimer,
}: OperationsProps) => {
  const { formatMessage, formatNumber, locale } = useIntl();
  const { operationsLink } = useInsightSubRoutes();

  const selectedRejectedOrders = rejectedOrdersInsight.selected.data;
  const previousRejectedOrders = rejectedOrdersInsight.previous.data;
  const selectedRejections = rejectionsInsight.selected.data;
  const previousRejections = rejectionsInsight.previous.data;
  const selectedPreparation = preparationInsight.selected.data;
  const previousPreparation = preparationInsight.previous.data;
  const selectedOfflineTime = offlineTimeInsight.selected.data;
  const previousOfflineTime = offlineTimeInsight.previous.data;
  const selectedRefundsOverview = refundsOverviewInsight.selected.data;
  const previousRefundsOverview = refundsOverviewInsight.previous.data;

  const preparationTime = selectedPreparation?.data?.preparationTime?.total.averagePreparationTime ?? 0;
  const preparationTimePrev = previousPreparation?.data?.preparationTime?.total.averagePreparationTime ?? 0;
  const rejectedOrders = selectedRejectedOrders?.data?.rejections?.total.orders ?? 0;
  const rejectedOrdersPrev = previousRejectedOrders?.data?.rejections?.total.orders ?? 0;
  const rejectionGrowthRates =
    selectedRejections?.data.rejections?.total && previousRejections?.data.rejections?.total
      ? getGrowthRateForFields(selectedRejections.data.rejections.total, previousRejections.data.rejections.total, [
          'orders',
          'salesNoVat',
          'salesWithVat',
        ])
      : undefined;
  const unavailableMinutes = selectedOfflineTime?.data?.offlineTime?.total.unavailableMinutes ?? 0;
  const unavailableMinutesPrev = previousOfflineTime?.data?.offlineTime?.total.unavailableMinutes ?? 0;

  const missingOrIncorrectItems = selectedRefundsOverview?.data?.overviewData?.total.count ?? 0;
  const missingOrIncorrectItemsPrev = previousRefundsOverview?.data?.overviewData?.total.count ?? 0;

  return (
    <SummaryTable
      isFetching={preparationInsight.isFetching || rejectionsInsight.isFetching}
      isError={preparationInsight.isError || rejectionsInsight.isError}
      onRetry={() => {
        preparationInsight.isError && preparationInsight.refetch();
        rejectionsInsight.isError && rejectionsInsight.refetch();
      }}
      title={formatMessage({ id: 'insights.operations.header' })}
      tooltip={formatMessage({ id: 'insights.operations.tooltip' })}
      link={operationsLink}
      rows={[
        {
          title: 'insights.operations.rejected-orders',
          titleTooltip: 'insights.rejected-orders.tooltip',
          type: RowType.Metric,
          metrics: rejectedOrders,
          growthRate: formatGrowthRate(calculateGrowthRate(rejectedOrdersPrev, rejectedOrders), locale),
          growthTrend: getGrowthTrend(calculateGrowthRate(rejectedOrdersPrev, rejectedOrders), true),
        },
        {
          title: 'insights.operations.loss-from-rejections',
          titleTooltip: 'insights.operations.loss-from-rejections-tooltip',
          metrics: formatNumber(getSales(selectedRejections?.data.rejections?.total, isVATEnabled) ?? 0, {
            currency: selectedRejections?.currency,
            style: 'currency',
          }),
          growthRate: formatGrowthRate(getSales(rejectionGrowthRates, isVATEnabled), locale),
          growthTrend: getGrowthTrend(getSales(rejectionGrowthRates, isVATEnabled), true),
          type: RowType.Metric,
        },
        ...(hidePrepTime
          ? []
          : [
              {
                title: showHybridDeliveryDisclaimer
                  ? 'insights.operations.preparation-time-of-wolt-delivery-orders'
                  : 'insights.operations.preparation-time',
                titleTooltip: showHybridDeliveryDisclaimer
                  ? 'insights.preparation-time.tooltip-hybrid-delivery'
                  : 'insights.preparation-time.tooltip',
                metrics: formatSeconds(preparationTime),
                growthRate: formatGrowthRate(calculateGrowthRate(preparationTimePrev, preparationTime), locale),
                growthTrend: getGrowthTrend(calculateGrowthRate(preparationTimePrev, preparationTime), true),
                type: RowType.Metric,
              } as const,
            ]),
        ...(isNewAlivenessEnabled
          ? [
              {
                title: 'insights.offline-time.unavailable-hours',
                titleTooltip: 'insights.offline-time.unavailable-hours-tooltip',
                metrics: formatMinutes(unavailableMinutes),
                growthRate: formatGrowthRate(calculateGrowthRate(unavailableMinutesPrev, unavailableMinutes), locale),
                growthTrend: getGrowthTrend(calculateGrowthRate(unavailableMinutesPrev, unavailableMinutes), true),
                type: RowType.Metric,
              } as const,
            ]
          : []),
        ...(isRefundOverviewEnabled
          ? [
              {
                title: 'insights.operations.missing-or-incorrect-items',
                metrics: missingOrIncorrectItems,
                growthRate: formatGrowthRate(
                  calculateGrowthRate(missingOrIncorrectItemsPrev, missingOrIncorrectItems),
                  locale,
                ),
                growthTrend: getGrowthTrend(
                  calculateGrowthRate(missingOrIncorrectItemsPrev, missingOrIncorrectItems),
                  true,
                ),
                type: RowType.Metric,
              } as const,
            ]
          : []),
      ]}
    />
  );
};

export default Operations;
